/**
 * Employee Types for MEV-Med System
 * Alinhado com backend: Employee model + relacionamentos
 */

// ==================== ENUMS ====================

export enum EmployeeStatus {
  ATIVO = 'ATIVO',
  AFASTADO = 'AFASTADO',
  DEMITIDO = 'DEMITIDO',
  INATIVO = 'INATIVO',
  SUSPENSO = 'SUSPENSO',
  LICENCA = 'LICENCA'
}

export enum EmployeeGender {
  M = 'M',
  F = 'F',
  O = 'O'
}

export enum MaritalStatus {
  SOLTEIRO = 'SOLTEIRO',
  CASADO = 'CASADO',
  DIVORCIADO = 'DIVORCIADO',
  VIUVO = 'VIUVO',
  UNIAO_ESTAVEL = 'UNIAO_ESTAVEL'
}

export enum SocialSecurityClassification {
  NAO_ENSEJADOR = 'NAO_ENSEJADOR',
  FAE15_12 = 'FAE15_12',
  FAE20_9 = 'FAE20_9',
  FAE25_6 = 'FAE25_6',
}

export const SOCIAL_SECURITY_CLASSIFICATION_LABELS: Record<SocialSecurityClassification, string> = {
  [SocialSecurityClassification.NAO_ENSEJADOR]: '1 - Não ensejador de aposentadoria especial',
  [SocialSecurityClassification.FAE15_12]: '2 - Ensejador de Aposentadoria Especial',
  [SocialSecurityClassification.FAE20_9]: '3 - Ensejador de Aposentadoria Especial',
  [SocialSecurityClassification.FAE25_6]: '4 - Ensejador de Aposentadoria Especial',
};

/** Código 1 a 4 do S-1200 / GFIP que o servidor calcula pelos riscos do cargo. */
export type CodigoAposentadoriaEspecial = 1 | 2 | 3 | 4;

/**
 * A correspondência 1:1 entre o código e o valor do Enquadramento. É só um dicionário: a regra
 * que leva do tempo de aposentadoria ao código mora no servidor, e copiá-la para cá criaria duas
 * tabelas que podem divergir.
 */
export const ENQUADRAMENTO_POR_CODIGO: Record<CodigoAposentadoriaEspecial, SocialSecurityClassification> = {
  1: SocialSecurityClassification.NAO_ENSEJADOR,
  2: SocialSecurityClassification.FAE15_12,
  3: SocialSecurityClassification.FAE20_9,
  4: SocialSecurityClassification.FAE25_6,
};

export function codigoDoEnquadramento(
  enquadramento: SocialSecurityClassification | ''
): CodigoAposentadoriaEspecial | null {
  for (const [codigo, valor] of Object.entries(ENQUADRAMENTO_POR_CODIGO)) {
    if (valor === enquadramento) return Number(codigo) as CodigoAposentadoriaEspecial;
  }
  return null;
}

/** "Tipo de Empresa" (label na UI) = posto de trabalho do funcionário. */
export enum WorkLocation {
  MATRIZ = 'MATRIZ',
  FILIAL = 'FILIAL',
  UNIDADE = 'UNIDADE',
}

export const WORK_LOCATION_LABELS: Record<WorkLocation, string> = {
  [WorkLocation.MATRIZ]: 'Matriz',
  [WorkLocation.FILIAL]: 'Filial',
  [WorkLocation.UNIDADE]: 'Unidade',
};

// ==================== INTERFACES PRINCIPAIS ====================

export interface Employee {
  id: number;
  
  // Dados pessoais
  name: string;
  socialName?: string;
  cpf: string;
  birthDate: string; // ISO date
  motherName?: string;
  gender: EmployeeGender;
  maritalStatus?: MaritalStatus;
  birthplace?: string;
  
  // Documentação
  cnh?: string;
  cnhExpiration?: string; // ISO date
  
  // Contato
  whatsapp?: string;
  email?: string;
  zipCode: string;
  address?: string;
  addressNumber?: string;
  neighborhood?: string;
  city?: string;
  state: string;
  
  // Dados ocupacionais
  companyId: number;
  departmentId: number;
  occupationId: number;
  employeeCode?: string; // Matrícula
  admissionDate?: string; // ISO date
  dismissalDate?: string; // ISO date
  leaveDate?: string; // ISO date
  workLocation?: WorkLocation;
  unitId?: number | null;
  unit?: { id: number; name: string } | null;
  workRegime?: string;
  cboCode?: string;
  socialSecurityClassification?: SocialSecurityClassification;

  // Relacionamentos (incluídos nas queries)
  company?: {
    id: number;
    legalName: string;
    tradeName?: string;
  };
  department?: {
    id: number;
    name: string;
    /** A unidade do setor: é por ela que o aplicado à unidade chega ao funcionário (#541). */
    unitId?: number | string | null;
  };
  occupation?: {
    id: number;
    name: string;
    cbo?: string;
  };
  
  // Dados relacionados (quando includeRelations=true)
  documents?: EmployeeDocuments;
  emergencyContacts?: EmployeeEmergencyContact[];
  cipa?: EmployeeCIPA;
  esocial?: EmployeeESocial;
  
  // PCD
  hasDisability?: string; // 'Sim' | 'Não'
  cidCode?: string;
  disabilityDescription?: string;
  
  // Status
  status: EmployeeStatus;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// ==================== RELACIONAMENTOS ====================

export interface EmployeeDocuments {
  id?: number;
  employeeId: number;
  ctps?: string;
  serieCtps?: string;
  ufCtps?: string;
  pis?: string;
}

export interface EmployeeEmergencyContact {
  id?: number;
  employeeId?: number;
  name: string;
  phone: string;
  relationship?: string;
  isPrimary?: boolean;
  // Endereço completo
  zipCode?: string;
  address?: string;
  addressNumber?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

export interface EmployeeCIPA {
  id?: number;
  employeeId: number;
  isMember: boolean;
  startDate?: string; // ISO date
  endDate?: string; // ISO date
  role?: string;
  term?: string;
}

export interface EmployeeESocial {
  id?: number;
  employeeId: number;
  inicioCondEsocial?: string; // ISO date
  fimCondEsocial?: string; // ISO date
  motivo?: string;
  gfip?: string;
  estabilidade?: string; // ISO date
}

// ==================== REQUEST TYPES ====================

export interface CreateEmployeeRequest {
  // Dados pessoais (obrigatórios)
  name: string;
  cpf: string;
  birthDate: string;
  gender: EmployeeGender;
  motherName: string;

  // Dados pessoais (opcionais)
  socialName?: string;
  maritalStatus?: MaritalStatus;
  birthplace?: string;

  // Documentação
  ctps?: string;
  serieCtps?: string;
  ufCtps?: string;
  cnh?: string;
  cnhExpiration?: string;

  // Contato (obrigatórios)
  zipCode: string;
  state: string;
  
  // Contato (opcionais)
  whatsapp?: string;
  email?: string;
  address?: string;
  addressNumber?: string;
  neighborhood?: string;
  city?: string;
  
  // Dados ocupacionais (obrigatórios)
  companyId: number;
  departmentId: number;
  occupationId: number;
  
  // Dados ocupacionais (opcionais)
  employeeCode?: string;
  admissionDate?: string;
  dismissalDate?: string;
  leaveDate?: string;
  workLocation?: WorkLocation;
  unitId?: number | null;
  workRegime?: string;
  cboCode?: string;

  // PCD
  hasDisability?: string;
  cidCode?: string;
  disabilityDescription?: string;

  // Relacionamentos opcionais (enviados no mesmo request)
  documents?: EmployeeDocuments;
  emergencyContacts?: EmployeeEmergencyContact[];
  cipa?: EmployeeCIPA;
  esocial?: EmployeeESocial;
}

export interface UpdateEmployeeRequest extends Partial<CreateEmployeeRequest> {
  status?: EmployeeStatus;
}

// ==================== FILTERS & PAGINATION ====================

export interface EmployeeFilters {
  search?: string; // Nome, CPF ou matrícula
  status?: EmployeeStatus | EmployeeStatus[];
  gender?: EmployeeGender | EmployeeGender[];
  companyId?: number | number[];
  departmentId?: number | number[];
  occupationId?: number | number[];
  hasDisability?: boolean;
  isMinor?: boolean; // Menor de 18 anos
  minAge?: number;
  maxAge?: number;
  state?: string | string[];
  city?: string;
}

export interface EmployeeListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  filters?: EmployeeFilters;
}

// ==================== FORM DATA ====================

export interface EmployeeFormData {
  // Seção: Dados Pessoais
  name: string;
  socialName: string;
  cpf: string;
  birthDate: string;
  motherName: string;
  gender: EmployeeGender | '';
  maritalStatus: MaritalStatus | '';
  birthplace: string;
  
  // Seção: Documentação
  cnh: string;
  cnhExpiration: string;
  ctps: string;
  serieCtps: string;
  ufCtps: string;
  pis: string;
  
  // Seção: Contato
  whatsapp: string;
  email: string;
  zipCode: string;
  address: string;
  addressNumber: string;
  neighborhood: string;
  city: string;
  state: string;
  
  // Seção: Contatos de Emergência
  emergencyContacts: EmployeeEmergencyContact[];
  
  // Seção: Dados Ocupacionais
  companyId: number | '';
  departmentId: number | '';
  occupationId: number | '';
  employeeCode: string;
  admissionDate: string;
  dismissalDate: string;
  leaveDate: string;
  workLocation: WorkLocation | '';
  unitId: number | '';

  // Seção: PCD
  hasDisability: string; // 'Sim' | 'Não' | ''
  cidCode: string;
  disabilityDescription: string;
  
  // Seção: CIPA
  cipa: {
    isMember: boolean;
    startDate: string;
    endDate: string;
    role: string;
    term: string;
  };
  
  // Seção: Enquadramento Previdenciário
  socialSecurityClassification: SocialSecurityClassification | '';

  // Seção: eSocial
  esocial: {
    inicioCondEsocial: string;
    fimCondEsocial: string;
    motivo: string;
    gfip: string;
    estabilidade: string;
  };
  
  // Status
  status: EmployeeStatus;
}

// ==================== LABELS & OPTIONS ====================

export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
  [EmployeeStatus.ATIVO]: 'Ativo',
  [EmployeeStatus.AFASTADO]: 'Afastado',
  [EmployeeStatus.DEMITIDO]: 'Demitido',
  [EmployeeStatus.INATIVO]: 'Inativo',
  [EmployeeStatus.SUSPENSO]: 'Suspenso',
  [EmployeeStatus.LICENCA]: 'Licença'
};

export const EMPLOYEE_STATUS_COLORS: Record<EmployeeStatus, string> = {
  [EmployeeStatus.ATIVO]: '#4CAF50',      // Verde
  [EmployeeStatus.AFASTADO]: '#FFC107',   // Amarelo
  [EmployeeStatus.DEMITIDO]: '#F44336',   // Vermelho
  [EmployeeStatus.INATIVO]: '#9E9E9E',    // Cinza
  [EmployeeStatus.SUSPENSO]: '#FF9800',   // Laranja
  [EmployeeStatus.LICENCA]: '#2196F3'     // Azul
};

export const GENDER_LABELS: Record<EmployeeGender, string> = {
  [EmployeeGender.M]: 'Masculino',
  [EmployeeGender.F]: 'Feminino',
  [EmployeeGender.O]: 'Outro'
};

export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  [MaritalStatus.SOLTEIRO]: 'Solteiro(a)',
  [MaritalStatus.CASADO]: 'Casado(a)',
  [MaritalStatus.DIVORCIADO]: 'Divorciado(a)',
  [MaritalStatus.VIUVO]: 'Viúvo(a)',
  [MaritalStatus.UNIAO_ESTAVEL]: 'União Estável'
};

// ==================== UTILITY TYPES ====================

export interface EmployeeStats {
  total: number;
  byStatus: Record<EmployeeStatus, number>;
  byGender: Record<EmployeeGender, number>;
  byDepartment: Record<string, number>;
  byOccupation: Record<string, number>;
  pcdCount: number;
  cipaCount: number;
  minorsCount: number;
}

export interface EmployeeExamStatus {
  total: number;
  valid: number;
  nearExpiry: number; // < 60 dias
  expired: number;
}

export interface EmployeeWithExamStatus extends Employee {
  examStatus?: EmployeeExamStatus;
}
